export interface FlightActivity {
  trace: (msg?: unknown) => void;
}

export interface FlightActivities {
  /** Logged when the user is in the experiment flight. */
  flightActivity: FlightActivity;
  /** Logged when the user is not in any flight. */
  nonFlightActivity: FlightActivity;
  /** Traces parse/fetch lifecycle for all flight data. */
  allFlightActivity: FlightActivity;
}

/**
 * The instrumentation sink used across the app — the `InstrumentationContext` API exposed by both
 * the React flight hooks and the legacy (Backbone) grid. Optional so a missing sink degrades to a
 * no-op.
 */
export interface FlightInstrumentation {
  createScenario?: (options: { scenarioName: string }) => {
    createActivity: (activityName: string) => FlightActivity;
  };
}

const noOpActivity: FlightActivity = { trace: () => {} };

/**
 * Builds the three flight activities (`<scenario>`, `<scenario>-NoExperiment`, `<scenario>-All`)
 * from an instrumentation sink, without depending on React. Shared by `useFlightInstrumentation`
 * (hook path) and `getAccountFlightStatusSync` (non-hook path) so both emit identical telemetry.
 *
 * @param {FlightInstrumentation} instrumentation - Instrumentation sink, or undefined for no-op.
 * @param {string} scenarioName - Scenario name; activity names derive from it.
 * @returns {FlightActivities} The flight / non-flight / all activities.
 */
export const createFlightInstrumentation = (
  instrumentation: FlightInstrumentation | undefined | null,
  scenarioName: string
): FlightActivities => {
  const scenario = instrumentation?.createScenario?.({ scenarioName });
  if (!scenario?.createActivity) {
    return {
      flightActivity: noOpActivity,
      nonFlightActivity: noOpActivity,
      allFlightActivity: noOpActivity,
    };
  }

  return {
    flightActivity: scenario.createActivity(scenarioName),
    nonFlightActivity: scenario.createActivity(`${scenarioName}-NoExperiment`),
    allFlightActivity: scenario.createActivity(`${scenarioName}-All`),
  };
};
